import React, { useState, useEffect, ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from 'util/UserContext';
import TextEditor from 'components/TextEditor';
import api from 'config/axiosConfig';
import { Meeting, Issue, Influence, Solution, Comment, Feedback, Team, Suggestion } from 'util/constants';
import Modal from 'ui/Modal';
import InputTextArea from 'ui/InputTextArea';
import ScrollableTabsList from 'ui/ScrollableTabsList';
import IssueSearch from 'components/Issues/IssueSearch';
import InfluenceSearch from 'components/Influences/InfluenceSearch';
import { Info, MapPin, Calendar, Lock, Clock } from 'lucide-react';
import CoauthorSelect, { CoauthorSuggestion } from 'components/Coauthors/CoauthorSelect';
import EntityAvatar from 'components/Entities/EntityAvatar';
import EntityPrivacy from 'components/Entities/EntityPrivacy';
import { PrivacyLevel } from 'util/userUtils';
import { format, setHours, setMinutes } from 'date-fns';
import { cn } from 'util/cn';
import { Button } from 'ui/Button';
import { Calendar as CalendarComponent } from 'ui/Calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'ui/Popover';

// Create a custom DatePicker component that includes time selection
const CustomDatePicker: React.FC<{
  value: Date | null;
  onChange: (date: Date | null) => void;
  minDate?: Date;
  placeholder?: string;
}> = ({ value, onChange, minDate, placeholder }) => {
  const [timeValue, setTimeValue] = useState<string>(
    value ? format(value, "HH:mm") : "12:00"
  );
  const [isOpen, setIsOpen] = useState(false);
  
  // Update time when the date changes
  useEffect(() => {
    if (value) {
      setTimeValue(format(value, "HH:mm"));
    }
  }, [value]);

  // Handle time input change
  const handleTimeChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newTimeValue = e.target.value;
    setTimeValue(newTimeValue);
    
    if (!value) return;
    
    try {
      const [hours, minutes] = newTimeValue.split(":").map(n => parseInt(n, 10));
      const newDate = setHours(setMinutes(value, minutes), hours);
      onChange(newDate);
    } catch (error) {
      console.error("Error parsing time:", error);
    }
  };
  
  // Handle date selection
  const handleDaySelect = (day: Date | undefined) => {
    if (!day) {
      onChange(null);
      return;
    }
    
    try {
      const [hours, minutes] = timeValue.split(":").map(n => parseInt(n, 10));
      const newDate = setHours(setMinutes(day, minutes), hours);
      onChange(newDate);
    } catch (error) {
      console.error("Error setting time on selected date:", error);
      onChange(day);
    }
  };

  return (
    <div>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant={"outline"}
            className={cn(
              "w-full justify-between text-left font-normal",
              !value && "text-muted-foreground"
            )}
          >
            <div className="flex items-center">
              <Calendar className="mr-2 h-4 w-4" />
              {value ? format(value, "PPP p") : <span>{placeholder || "Pick date and time"}</span>}
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 z-[2000]" align="start">
          <div className="p-3 border-b">
            <div className="flex justify-between items-center mb-2">
              <div className="font-medium text-sm">Select date & time</div>
              {value && (
                <div className="text-xs text-muted-foreground">
                  {format(value, "PPP p")}
                </div>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <input
                type="time"
                value={timeValue}
                onChange={handleTimeChange}
                className="input-text p-1 h-8 text-sm"
              />
            </div>
          </div>
          <CalendarComponent
            mode="single"
            selected={value || undefined}
            onSelect={handleDaySelect}
            initialFocus
            disabled={(date) => {
              if (minDate && date < minDate) return true;
              return false;
            }}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
};

interface Place {
  is_virtual: boolean;
  location: {
    event_url?: string;
    address?: string;
    latitude?: number;
    longitude?: number;
  };
  note?: string;
}

interface Props {
  entity?: Issue | Influence | Solution | Comment | Feedback | Team | null;
  entityType?: 'issue' | 'influence' | 'solution' | 'comment' | 'feedback' | 'team' | null;
  onMeetingAdded: (meeting: Meeting) => void;
  open: boolean;
  onClose: () => void;
}

const AddMeeting: React.FC<Props> = ({ entity, entityType, onMeetingAdded, open, onClose }) => {
  const { currentUser, awaitingUser, setShowAuthModal } = useUser();
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState<string>(entityType || 'issue');
  const [title, setTitle] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [startAt, setStartAt] = useState<Date | null>(null);
  const [endAt, setEndAt] = useState<Date | null>(null);
  const [isPrivate, setIsPrivate] = useState<boolean>(false);
  const [places, setPlaces] = useState<Place[]>([{
    is_virtual: true,
    location: { event_url: '' },
    note: ''
  }]);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [newMeeting, setNewMeeting] = useState<Meeting | null>(null);
  const [titleValidationMessage, setTitleValidationMessage] = useState<string>('')
  const [submitEnabled, setSubmitEnabled] = useState<boolean>(false);
  const [meetingEntityType, setMeetingEntityType] = useState<'issue' | 'influence' | 'solution' | 'comment' | 'feedback' | 'team' | null>(entityType ? entityType : null);
  const [meetingEntity, setMeetingEntity] = useState<Issue | Influence | Solution | Comment | Feedback | Team | null>(entity || null);
  const [organizers, setOrganizers] = useState<CoauthorSuggestion[]>([]);
  const [privacy, setPrivacy] = useState<PrivacyLevel>('public');

  const tabs = [
    { label: "Issue", value: "issue" },
    { label: "Influence", value: "influence" },
    { label: "Solution", value: "solution" },
    { label: "Team", value: "team" }
  ];

  useEffect(() => {
    if (awaitingUser) {
      return;
    }
    if (currentUser && currentUser?.properties?.privacy_content_default_audience) {
      setPrivacy(currentUser?.properties?.privacy_content_default_audience.value);
    } else {
      setPrivacy('public');
    }
  }, [currentUser, awaitingUser]);

  const handleAddMeeting = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!currentUser) {
      setShowAuthModal(true);
      return;
    }

    if (!title.trim() || !startAt) {
      setError('Title and start date are required.');
      setSuccess('');
      return;
    }

    setIsSubmitting(true);
    setError('');
    setSuccess('');
    setTitleValidationMessage('');

    try {
      // Prepare properties object
      const properties: Record<string, any> = {
        start_at: startAt?.toISOString(),
        end_at: endAt?.toISOString(),
        places: places,
        is_private: isPrivate,
        organizers: [
          {
            id: currentUser.id,
            type: 'user',
            status: 'active',
            created_at: new Date().toISOString(),
            set_at: new Date().toISOString()
          }
        ],
        sponsors: [],
        entity_type: meetingEntityType,
        entity_id: meetingEntity?.id
      };

      const meetingData = {
        title: title.trim(),
        description: description.trim(),
        properties,
        properties_public: { privacy },
        entityType: meetingEntityType,
        entityId: meetingEntity?.id
      };
      
      const response = await api.post<Meeting>('/api/meetings', meetingData);
      const newMeeting = response.data;
      
      setNewMeeting(newMeeting);

      // Add organizers if any
      if (organizers.length > 0) {
        try {
          await api.post(`/api/meetings/${newMeeting.id}/organizers`, {
            addedOrganizers: organizers.map(c => ({
              id: c.id,
              type: c.type
            }))
          });
        } catch (err) {
          console.error('Error adding organizers:', err);
          setError('Failed to add organizers. Please try again.');
          setIsSubmitting(false);
          return;
        }
      }

      setSuccess('Event added successfully!');
      onMeetingAdded(newMeeting);
      handleCloseModal();
      navigate(`/event/${newMeeting.id}`);
    } catch (err: any) {
      if (err.response?.status === 401) {
        setShowAuthModal(true);
      } else {
        setError('Failed to add event. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const isValidSubmit = () => {
      setTitleValidationMessage('');
      
      // Title must be present
      if (!title || title.length === 0) {
        setTitleValidationMessage('Title is required.');
        return false;
      }

      // Start date is required
      if (!startAt) {
        return false;
      }

      // End date must be after start date if provided
      if (startAt && endAt && endAt < startAt) {
        return false;
      }

      // At least one place is required with valid data
      if (places.length === 0) {
        return false;
      }

      for (const place of places) {
        if (place.is_virtual && (!place.location.event_url || place.location.event_url.trim() === '')) {
          return false;
        }
        if (!place.is_virtual && (!place.location.address || place.location.address.trim() === '')) {
          return false;
        }
      }

      // Not currently submitting
      if (isSubmitting) {
        return false;
      }

      return true;
    };

    setSubmitEnabled(isValidSubmit());
    setTitleValidationMessage('');
  }, [
    title,
    startAt,
    endAt,
    places,
    isSubmitting
  ]);

  const handleEntitySelect = (suggestion: Suggestion, type: string) => {
    setMeetingEntity(suggestion as any);
    setMeetingEntityType(type as any);
  };

  const handleEntityChipClose = () => {
    setMeetingEntity(null);
  };

  const handleTabChange = (tab: string) => {
    setSelectedTab(tab);
    setMeetingEntity(null);
    setMeetingEntityType(tab as any);
  };

  const handleCloseModal = () => {
    onClose();
    setTitle('');
    setDescription('');
    setStartAt(null);
    setEndAt(null);
    setIsPrivate(false);
    setPlaces([{
      is_virtual: true,
      location: { event_url: '' },
      note: ''
    }]);
    setError('');
    setSuccess('');
    setTitleValidationMessage('');
    setOrganizers([]);
  };

  const handleOrganizerSelect = (suggestion: CoauthorSuggestion) => {
    if (!organizers.some(c => c.id === suggestion.id && c.type === suggestion.type)) {
      setOrganizers([...organizers, suggestion]);
    }
  };

  const handleOrganizerRemove = (organizer: CoauthorSuggestion) => {
    setOrganizers(organizers.filter(c => !(c.id === organizer.id && c.type === organizer.type)));
  };

  const handlePlaceChange = (index: number, field: string, value: any) => {
    const updatedPlaces = [...places];
    
    if (field === 'is_virtual') {
      updatedPlaces[index] = {
        ...updatedPlaces[index],
        is_virtual: value,
        location: value 
          ? { event_url: '' } 
          : { address: '', latitude: undefined, longitude: undefined }
      };
    } else if (field === 'event_url') {
      updatedPlaces[index] = {
        ...updatedPlaces[index],
        location: { ...updatedPlaces[index].location, event_url: value }
      };
    } else if (field === 'address') {
      updatedPlaces[index] = {
        ...updatedPlaces[index],
        location: { ...updatedPlaces[index].location, address: value }
      };
    } else if (field === 'coordinates') {
      updatedPlaces[index] = {
        ...updatedPlaces[index],
        location: { 
          ...updatedPlaces[index].location, 
          latitude: value.lat, 
          longitude: value.lng,
          address: value.address || updatedPlaces[index].location.address
        }
      };
    } else if (field === 'note') {
      updatedPlaces[index] = {
        ...updatedPlaces[index],
        note: value
      };
    }
    
    setPlaces(updatedPlaces);
  };

  //TODO: remove arbitrary and use the newMeeting object
  console.log(newMeeting);

  const addPlace = () => {
    setPlaces([...places, {
      is_virtual: true,
      location: { event_url: '' },
      note: ''
    }]);
  };

  const removePlace = (index: number) => {
    if (places.length > 1) {
      const updatedPlaces = [...places];
      updatedPlaces.splice(index, 1);
      setPlaces(updatedPlaces);
    }
  };

  return (
    <Modal isOpen={open} onClose={handleCloseModal} title="Add Event">
      <div className="flex flex-col h-[calc(100%-44px)] max-h-[calc(100%-44px)] sm:h-auto overflow-y-scroll sm:overflow-y-auto overflow-x-hidden space-y-4">
        <div className="flex flex-col flex-grow space-y-4">
            <div className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
                Under development - may not function properly.
            </div>
          <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
            {!entityType && `Search for an ${selectedTab}.`} Create an event for this {selectedTab}. Events can be used for organizing protests, economic blackouts, or just to further discuss an issue or solution.
          </p>
          {!entityType && (
          <ScrollableTabsList
            tabs={tabs}
            navlink={false}
            selectedTab={selectedTab}
            pageTitle="Add Event"
            onTabChange={handleTabChange}
            className=""
          />
          )}

          <form className="flex flex-col flex-grow space-y-4">
            {/* Entity Search Field */}
            <div className="form-control mb-0">
              {selectedTab === 'issue' ? (
                <IssueSearch
                  inputType="add"
                  placeholder="Issue"
                  className="w-full"
                  fieldRequired={true}
                  prepopulateData={selectedTab === 'issue' ? entity as Issue : null}
                  validationState="none"
                  onSelect={(suggestion) => handleEntitySelect(suggestion, 'issue')}
                  onEntityChipClose={handleEntityChipClose}
                />
              ) : selectedTab === 'influence' ? (
                <InfluenceSearch
                  inputType="add"
                  placeholder="Influence"
                  className="w-full"
                  fieldRequired={true}
                  prepopulateData={selectedTab === 'influence' ? entity as Influence : null}
                  validationState="none"
                  onSelect={(suggestion) => handleEntitySelect(suggestion as any, 'influence')}
                  onEntityChipClose={handleEntityChipClose}
                />
              ) : (
                <div className="text-sm text-red-500">
                  Search for {selectedTab} not yet implemented
                </div>
              )}
            </div>

            {/* Title Field */}
            <div className="form-control mb-0">
              <InputTextArea
                id="meeting-title"
                name="meeting-title"
                labelText="Title"
                fieldRequired
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                autoComplete="meeting-title"
                required
                maxLength={300}
                error={titleValidationMessage}
                validationState={titleValidationMessage ? 'invalid' : !titleValidationMessage && title && title.length > 0 ? 'valid' : 'none'}
              />
            </div>

            {/* Date/Time Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="form-control">
                <label className="form-label flex gap-1">
                  <Calendar className="h-4 w-4" />
                  <span>Start Date/Time</span>
                  <span className="text-warning-red dark:text-dark-warning-red">*</span>
                </label>
                <CustomDatePicker 
                  value={startAt} 
                  onChange={setStartAt}
                  minDate={new Date()}
                  placeholder="Select start date/time"
                />
              </div>
              <div className="form-control">
                <label className="form-label flex gap-1">
                  <Calendar className="h-4 w-4" />
                  <span>End Date/Time</span>
                </label>
                <CustomDatePicker 
                  value={endAt} 
                  onChange={setEndAt}
                  minDate={startAt || new Date()}
                  placeholder="Select end date/time"
                />
              </div>
            </div>

            {/* Places */}
            <div className="form-control">
              <label className="form-label mb-2">
                <div className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  <span>Location</span>
                  <span className="text-warning-red dark:text-dark-warning-red">*</span>
                </div>
              </label>
              {places.map((place, index) => (
                <div key={index} className="mb-4 p-3 border border-eoe-border-main dark:border-eoe-dark-border-main rounded-md">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                        <input
                        type="checkbox"
                        id="register"
                        checked={place.is_virtual}
                        onChange={(e) => {
                            handlePlaceChange(index, 'is_virtual', e.target.checked);
                        }}
                        className="toggle-switch-input col-span-1"
                        />
                    </div>
                    {places.length > 1 && (
                      <button 
                        type="button" 
                        onClick={() => removePlace(index)}
                        className="text-red-500 hover:text-red-700 text-sm"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  
                  {place.is_virtual ? (
                    <div className="mb-2">
                      <label className="block text-sm mb-1">Event URL</label>
                      <input
                        type="url"
                        className="input-text w-full"
                        value={place.location.event_url || ''}
                        onChange={(e) => handlePlaceChange(index, 'event_url', e.target.value)}
                        placeholder="https://zoom.us/j/123456789"
                        required
                      />
                    </div>
                  ) : (
                    <div className="mb-2">
                      <label className="block text-sm mb-1">Physical Address</label>
                      <input
                        type="text"
                        className="input-text w-full"
                        value={place.location.address || ''}
                        onChange={(e) => {
                          handlePlaceChange(index, 'address', e.target.value);
                          handlePlaceChange(index, 'coordinates', {
                            lat: 0,
                            lng: 0,
                            address: e.target.value
                          });
                        }}
                        placeholder="Enter address"
                        required
                      />
                    </div>
                  )}
                  
                  <div>
                    <label className="block text-sm mb-1">Notes</label>
                    <textarea
                      className="input-text w-full"
                      value={place.note || ''}
                      onChange={(e) => handlePlaceChange(index, 'note', e.target.value)}
                      placeholder="Additional information about the location"
                      rows={2}
                    />
                  </div>
                </div>
              ))}
              <button 
                type="button" 
                onClick={addPlace}
                className="btn-outline-primary w-full mt-2"
              >
                Add Another Location
              </button>
            </div>

            {/* Privacy Options */}
            <div className="form-control">
              <div className="flex items-center gap-2 mb-2">
                <div className="flex-1">
                  <div className="flex items-center gap-1">
                    <Lock className="h-4 w-4" />
                    <span className="form-label">Private Event</span>
                  </div>
                  <p className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
                    Private events are only visible to organizers and participants
                  </p>
                </div>
              </div>
              
              <EntityPrivacy
                privacy={privacy}
                onPrivacyChange={(newPrivacy) => setPrivacy(newPrivacy)}
                actorType="user"
                editMode={false}
              />
            </div>

            {/* Description Field */}
            <div className="form-control">
              <TextEditor
                value={description}
                onChange={setDescription}
                editorType="entity"
                placeholder="Event description"
              />
            </div>

            {/* Organizers Field */}
            <div className="form-control">
              <div className="flex flex-col gap-2">
                <label className="form-label">Event Organizers</label>
                <CoauthorSelect
                  inputType="add"
                  placeholder="Add organizers"
                  className="w-full"
                  onSelect={handleOrganizerSelect}
                  excludeIds={organizers.map(c => c.id)}
                />
                {organizers.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {organizers.map((organizer) => (
                      <EntityAvatar
                        key={`${organizer.type}-${organizer.id}`}
                        entity={organizer as any}
                        entityType={organizer.type}
                        entityChip={true}
                        showLink={false}
                        showCloseChip={true}
                        onEntityChipClose={() => handleOrganizerRemove(organizer)}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Error and Success Messages */}
            {(error || success) && (
              <div className="flex items-center space-x-2">
                {error && (
                  <div className="flex items-center text-warning-red dark:text-warning-red-dark text-sm">
                    <Info className="mr-1" />
                    <span>{error}</span>
                  </div>
                )}
                {success && (
                  <div className="flex items-center text-green-600 text-sm">
                    <Info className="mr-1" />
                    <span>{success}</span>
                  </div>
                )}
              </div>
            )}
          </form>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-grow flex-col pb-8 justify-end place-items-end mb-6 mt-auto space-y-2 sm:space-y-0 sm:space-x-2 sm:flex sm:justify-end sm:flex-row sm:mb-0">
          <button
            type="button"
            onClick={handleCloseModal}
            className="btn-secondary w-full sm:w-fit py-2 px-4"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleAddMeeting}
            disabled={isSubmitting || !submitEnabled}
            className={`w-full sm:w-fit py-2 px-4 ${
              isSubmitting || !submitEnabled ? 'btn-primary-disabled' : 'btn-primary'
            } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {isSubmitting ? 'Submitting...' : 'Create Event'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default AddMeeting;
